import { useMutation } from '@apollo/client';
import {
  InsertFormFieldPositionsDocument,
  UpdateFormFieldPositionsDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { AnimatePresence, motion, Reorder } from 'framer-motion';
import type { FC, ReactElement, ReactNode } from 'react';
import { isValidElement } from 'react';
import { useCallback, useEffect } from 'react';

import { evictField } from '@/utils/graphqlUtils';

import useControlledCustomAttributes from '../../custom-attributes/useControlledCustomAttributes';
import { ConditionalFieldsProvider } from '../conditional-fields-provider/ConditionalFieldsProvider';
import { useCustomisableFormDataContext } from '../customisable-form-data/CustomisableFormDataContext';
import DraggableField from './DraggableField';
import { useElementsOrder } from './hooks/useElementsOrder';
import { useTransformSchemaToRequiredFields } from './hooks/useTransformSchemaToRequiredFields';
import { useRiskSmartForm } from './RiskSmartFormContext';
import style from './style.module.scss';

type Props = {
  children: ReactNode | ReactNode[];
  readOnly?: boolean;
};

/**
 * Wraps a forms fields to support customisation of the order
 * @returns
 */
const CustomisableFieldWrapper: FC<Props> = ({
  children: childrenProp,
  readOnly,
}) => {
  const { parentType, editMode } = useRiskSmartForm();

  if (!parentType) {
    throw new Error(
      'CustomisableForm must have a parentType on the RiskSmartFormProvider'
    );
  }

  const [updateFormFieldPositions] = useMutation(
    UpdateFormFieldPositionsDocument,
    {
      update: (cache) => {
        evictField(cache, 'form_configuration');
        evictField(cache, 'form_field_ordering');
      },
    }
  );
  const [insertFormFieldPositions] = useMutation(
    InsertFormFieldPositionsDocument,
    {
      update: (cache) => {
        evictField(cache, 'form_configuration');
        evictField(cache, 'form_field_ordering');
      },
    }
  );

  const customAttributeElements = useControlledCustomAttributes({
    readOnly,
  });

  const { formFieldOrdering, customAttributeSchema } =
    useCustomisableFormDataContext();
  const childrenArray = Array.isArray(childrenProp)
    ? childrenProp
    : [childrenProp];
  const children = [...childrenArray, ...customAttributeElements];

  const elementsByKey = (
    children.filter((c) => isValidElement(c)) as ReactElement[]
  ).map((element) => {
    if (!element.key) {
      throw new Error(
        `All children of a CustomisableForm must have a unique 'key' prop, ${
          element.props.name ?? 'a child'
        } does not have one.`
      );
    }

    return {
      key: String(element.key),
      value: element,
    };
  });

  if (elementsByKey.some((e, i) => elementsByKey.indexOf(e) !== i)) {
    throw new Error("CustomisableForm's children must have unique 'key' props");
  }

  useTransformSchemaToRequiredFields(parentType);

  const { elementsOrder, visibleElements, setElementsOrder } = useElementsOrder(
    elementsByKey,
    formFieldOrdering ?? undefined
  );

  const isUpdate = !!customAttributeSchema;

  const saveFields = useCallback(async () => {
    if (!parentType || !elementsOrder) {
      return;
    }

    const mutateFn = isUpdate
      ? updateFormFieldPositions
      : insertFormFieldPositions;

    await mutateFn({
      variables: {
        fieldConfig: elementsOrder.map((key, i) => ({
          FormConfigurationParentType: isUpdate ? parentType : undefined,
          FieldId: key,
          Position: i,
          form: null,
        })),
        fieldIds: elementsOrder.map((key) => key),
        parentType,
      },
    });
  }, [
    elementsOrder,
    parentType,
    updateFormFieldPositions,
    insertFormFieldPositions,
    isUpdate,
  ]);

  const { setOnSave } = useRiskSmartForm();

  useEffect(() => {
    if (editMode) {
      setOnSave(() => saveFields);
    }

    return () => setOnSave(undefined);
  }, [saveFields, setOnSave, editMode]);

  return (
    <AnimatePresence mode={'wait'}>
      {
        <motion.div
          key={'fields'}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.05 }}
        >
          <Reorder.Group
            data-testid={'customisable-form-content'}
            className={style.reorderGroup}
            axis={'y'}
            values={elementsOrder}
            onReorder={setElementsOrder}
          >
            <div>
              {visibleElements
                ?.filter(
                  (key) =>
                    elementsByKey.find((e) => e.key === key)?.value !== null
                )
                .map((key) => (
                  <DraggableField key={key} value={key} draggable={editMode}>
                    {elementsByKey.find((e) => e.key === key)?.value}
                  </DraggableField>
                ))}
            </div>
          </Reorder.Group>
        </motion.div>
      }
    </AnimatePresence>
  );
};

const CustomisableFieldWrapperWithConditionalProvider: FC<Props> = (props) => (
  <ConditionalFieldsProvider>
    <CustomisableFieldWrapper {...props} />
  </ConditionalFieldsProvider>
);

export default CustomisableFieldWrapperWithConditionalProvider;
